import { useState, useMemo } from 'react'
import { CloudArrowUpIcon, InformationCircleIcon } from '@heroicons/react/24/solid'
import { Spinner } from '../../utils/Spinner'
import { getPimsDisplayName } from '../../utils/PimsUtils'
import { useHistory } from '../../providers/HistoryProvider'
import { Modal } from '../common/Modal'

interface StaffIdModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    pims: string;
    targetClerkId: string | undefined;
    targetUserIdentifier: string | undefined;
}

export default function StaffIdModal({
    isOpen,
    onClose,
    onConfirm,
    pims,
    targetClerkId: userId,
    targetUserIdentifier: userIdentifier,
}: StaffIdModalProps) {
    const [staffId, setStaffId] = useState<string>("");
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | undefined>(undefined);
    const { handleSaveOnPremPimsStaffId, importAppointmentsFromOnPremPims } = useHistory();
    const pimsDisplayName = useMemo(() => {
        return getPimsDisplayName(pims)
    }, [pims])

    async function handleSaveStaffId() {
        if (!staffId.trim() || !userId) {
            setError("Staff ID is required");
            return;
        }
        try {
            setSaving(true);
            await handleSaveOnPremPimsStaffId(staffId.trim(), userId);
            onConfirm();
            importAppointmentsFromOnPremPims(userId);
        } catch (err) {
            setError(`Failed to save Staff ID: ${err instanceof Error ? err.message : String(err)}`);
        }
        finally {
            setSaving(false);
            setStaffId("");
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={`${pimsDisplayName} Staff ID`}
            subTitle={
                <>
                    <span>
                        Please enter your {pimsDisplayName} Staff ID to sync appointments. This will be saved for future use.
                    </span>
                    {userIdentifier && userIdentifier !== "all" && (
                        <p className="mt-2 text-sm bg-blue-50 rounded-md p-2 text-blue-700">
                            Setting up Staff ID for: <span className="font-medium">{userIdentifier}</span>
                        </p>
                    )}
                </>
            }
            icon={<CloudArrowUpIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />}
            maxWidth="sm:max-w-sm"
        >
            {error && (
                <div className="mt-2 bg-red-100 rounded-md p-4 text-sm text-red-900 text-center flex flex-row gap-x-3 justify-center items-center mx-auto">
                    <InformationCircleIcon className="h-5 w-5 text-red-600" />
                    {error}
                </div>
            )}

            <div className="mt-5 sm:mt-6 flex flex-col gap-y-2 justify-center items-center relative w-full">
                <label
                    htmlFor="staff-id"
                    className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-main-text-darker z-[1]"
                >
                    Staff ID:
                </label>
                <input
                    id="staff-id"
                    className="rounded-md h-10 w-full border border-gray-300 px-2"
                    onChange={(event) => {
                        setError(undefined);
                        setStaffId(event.target.value);
                    }}
                    title='Staff ID'
                    placeholder={`${pimsDisplayName} Staff ID`}
                    disabled={saving}
                    value={staffId}
                />
            </div>

            <div className="mt-5 flex flex-row gap-x-3">
                <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600 h-10 items-center"
                    onClick={onClose}
                    disabled={saving}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 h-10 items-center ${
                        saving ? 'cursor-not-allowed bg-gray-600' : 'cursor-pointer bg-blue-600 hover:bg-blue-500'
                    }`}
                    onClick={handleSaveStaffId}
                    disabled={saving || !staffId.trim()}
                >
                    {saving ? <Spinner size='h-5 w-5' timer={false} /> : "Save & Continue"}
                </button>
            </div>
        </Modal>
    );
}
